import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Alert } from 'antd';
import { UserAddOutlined, MailOutlined } from '@ant-design/icons';
import './AddUserModal.scss';

const AddUserModal = ({ visible, onClose, onSubmit }) => {
const [form] = Form.useForm();
const [loading, setLoading] = useState(false);
const [error, setError] = useState('');

// 生成隨機密碼
const generateRandomPassword = () => {
  const length = 12;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

// Email 格式驗證規則 - 修改為只顯示一個錯誤信息
const emailValidationRules = [
  {
    required: true,
    message: 'Please enter email address'
  },
  {
    type: 'email',
    message: 'Please enter a valid email address'
  }
];

// 處理表單提交
const handleSubmit = async () => {
  try {
    // 驗證表單
    const values = await form.validateFields();
    
    setLoading(true);
    setError('');

    // 生成隨機密碼
    const randomPassword = generateRandomPassword();
    
    // 準備提交數據
    const submitData = {
      email: values.email.trim().toLowerCase(),
      password: randomPassword
    };

    // 調用父組件的提交函數
    await onSubmit(submitData);
    
    // 成功後重置表單並關閉模態框
    form.resetFields();
    message.success('User created successfully! The user can change their password in the broadcaster web.');
    onClose();
    
  } catch (error) {
    console.error('Add user error:', error);
    
    // 處理不同類型的錯誤
    if (error.response && error.response.data) {
      const errorMessage = error.response.data.message || error.response.data.error;
      
      if (errorMessage) { //  && errorMessage.toLowerCase().includes('email already exists')
        setError('This email address is already registered. Please use a different email.');
      } else {
        setError(errorMessage || 'Failed to create user. Please try again.');
      }
    } else if (error.errorFields) {
      // 表單驗證錯誤，不需要特別處理
      return;
    } else {
      setError('An unexpected error occurred. Please try again.');
    }
  } finally {
    setLoading(false);
  }
};

// 處理模態框關閉
const handleCancel = () => {
  form.resetFields();
  setError('');
  onClose();
};

// 處理輸入變化（清除錯誤信息）
const handleEmailChange = () => {
  if (error) {
    setError('');
  }
};

return (
  <Modal
    title={
      <div className="add-user-modal-title">
        <UserAddOutlined />
        Add New User
      </div>
    }
    open={visible}
    onCancel={handleCancel}
    footer={[
      <Button key="cancel" onClick={handleCancel}>
        Cancel
      </Button>,
      <Button
        key="submit"
        type="primary"
        loading={loading}
        onClick={handleSubmit}
        icon={<UserAddOutlined />}
      >
        Create User
      </Button>
    ]}
    width={500}
    destroyOnClose={true}
    maskClosable={false}
    className="add-user-modal"
  >
    <div className="add-user-modal-content">
      {/* 錯誤提示 */}
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          className="error-alert"
          closable
          onClose={() => setError('')}
        />
      )}

      {/* 說明文字 */}
      <Alert
        message="A random password will be generated automatically. The user can change their password in the broadcaster web."
        type="info"
        showIcon
        className="info-alert"
      />

      {/* 表單 */}
      <Form
        form={form}
        layout="vertical"
        size="large"
        className="add-user-form"
        validateFirst={true}
      >
        <Form.Item
          name="email"
          label="Email Address"
          rules={emailValidationRules}
          hasFeedback
          validateFirst={true}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="Enter user's email address"
            onChange={handleEmailChange}
            autoComplete="off"
          />
        </Form.Item>
      </Form>

      {/* 額外說明 */}
      <div className="user-management-note">
        <strong>Note:</strong> After creating the user, you can further edit their profile and permissions 
        through the user management interface. The user will be able to log in with their email and 
        change their password in the broadcaster web platform.
      </div>
    </div>
  </Modal>
);
};

export default AddUserModal;